import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Space,
  Row,
  Col,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PoweroffOutlined,
  ApiOutlined,
  DownloadOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import Heading from "../../../components/Heading";
import { ReportConfigService } from "../../../services/api/reportConfig";
import "./index.scss";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

// ── Helpers ─────────────────────────────────────────────────────────────────

function extractCols(sql = "") {
  const clean = sql.replace(/\s+/g, " ").trim();
  const m = clean.match(/(?:^|\s)SELECT\s+(.+?)\s+FROM\s/i);
  if (!m) return [];
  return splitParens(m[1])
    .map((p) => {
      p = p.trim();
      const alias = p.match(/\bAS\s+(\w+)\s*$/i);
      if (alias) return alias[1].toLowerCase();
      const tokens = p.split(/[\s.]+/);
      return tokens[tokens.length - 1].toLowerCase().replace(/[^a-z0-9_]/g, "");
    })
    .filter(Boolean);
}

function splitParens(s) {
  const r = [];
  let d = 0;
  let cur = "";
  for (const c of s) {
    if (c === "(") d++;
    else if (c === ")") d--;
    else if (c === "," && d === 0) { r.push(cur); cur = ""; continue; }
    cur += c;
  }
  if (cur) r.push(cur);
  return r;
}

function toTitleCase(snake) {
  return snake.split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : "")).join(" ");
}

function copyToClipboard(url) {
  navigator.clipboard
    .writeText(window.location.origin + url)
    .then(() => message.success("URL copied to clipboard"))
    .catch(() => message.info(url));
}

// ── SQL Preview sub-component ────────────────────────────────────────────────

function SqlPreview({ sql }) {
  const cols = extractCols(sql);
  const rows = [
    { label: "S.No", key: "_sno" },
    ...cols.map(c => ({ label: toTitleCase(c), key: c }))
  ];
  const isEmpty = !cols.length;

  if (isEmpty) {
    return (
      <div className="rcm-sql-preview-empty">
        <span role="img" aria-label="doc">📋</span> Column Mapping Preview <small>(auto from SQL)</small>
        <div style={{ color: "#8c8c8c", marginTop: 8, fontSize: 13 }}>Enter a valid SELECT query...</div>
      </div>
    );
  }

  return (
    <div className="rcm-simple-preview-container">
      <div className="rcm-preview-label">
        <span role="img" aria-label="doc">📋</span> Column Mapping Preview <small>(auto from SQL)</small>
      </div>
      <div className="rcm-simple-table-wrapper">
        <table className="rcm-simple-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Header Label (Title Case)</th>
              <th>Data Key (JSON API)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td>{i + 1}</td>
                <td>{r.label}</td>
                <td>{r.key}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

const ReportConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, split: 0 });

  // ── Data Loading ────────────────────────────────────────────────────────

  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const result = await ReportConfigService.getAll(page - 1, pageSize);
      setTableData(result.data);
      const pag = result.pagination;
      setPagination((prev) => ({
        ...prev,
        current: (pag.currentPage ?? page - 1) + 1,
        pageSize: pag.pageSize ?? pageSize,
        total: pag.totalRecords ?? pag.totalElements ?? result.data.length,
      }));
      const active = result.data.filter((r) => r.isActive).length;
      const split = result.data.filter((r) => r.downloadSql).length;
      setStats({
        total: pag.totalRecords ?? pag.totalElements ?? result.data.length,
        active,
        inactive: result.data.length - active,
        split,
      });
    } catch (err) {
      message.error("Failed to load report configs: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(1, 10);
  }, [fetchData]);

  const handleTableChange = (pag) => {
    fetchData(pag.current, pag.pageSize);
  };

  // ── Modal ───────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditKey(null);
    form.resetFields();
    form.setFieldsValue({ datasource: "REPLICATION", batchSize: 50000, isActive: true, filters: [] });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditKey(record.reportKey);
    // Reset first so Form.List remounts cleanly with new filter items
    form.resetFields();
    const filters = Array.isArray(record.filterConfigs)
      ? record.filterConfigs.map((f) => ({
        key: f.key || "",
        type: f.type || "exact",
        column: f.column || extractColumnFromClause(f.sqlClause),
      }))
      : [];
    form.setFieldsValue({
      reportKey: record.reportKey,
      displayName: record.displayName || "",
      datasource: record.datasource || "REPLICATION",
      batchSize: record.batchSize || 50000,
      sql: record.sql || "",
      downloadSql: record.downloadSql || "",
      isActive: record.isActive ?? true,
      filters,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditKey(null);
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editKey) {
        // ── PATCH: partial update – backend regenerates headers/columnKeys from correct SQL ──
        const patchPayload = {
          displayName: (values.displayName || values.reportKey).trim(),
          datasource: values.datasource,
          sql: values.sql.trim(),
          downloadSql: values.downloadSql?.trim() || null,
          batchSize: values.batchSize || 50000,
          isActive: values.isActive ?? true,
          filters: (values.filters || []).filter((f) => f.key && f.column),
        };
        await ReportConfigService.patch(editKey, patchPayload);
      } else {
        // ── POST upsert: create new report config ──
        const payload = {
          reportKey: values.reportKey.trim(),
          displayName: (values.displayName || values.reportKey).trim(),
          datasource: values.datasource,
          sql: values.sql.trim(),
          downloadSql: values.downloadSql?.trim() || null,
          batchSize: values.batchSize || 50000,
          isActive: values.isActive ?? true,
          filters: (values.filters || []).filter((f) => f.key && f.column),
        };
        await ReportConfigService.upsert(payload);
      }

      message.success(editKey ? "Config updated successfully!" : "Config created successfully!");
      closeModal();
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Save failed: " + (err.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Toggle / Delete ─────────────────────────────────────────────────────

  const handleToggle = async (reportKey, currentActive) => {
    try {
      const res = await ReportConfigService.toggleActive(reportKey);
      message.success(res?.message || `${currentActive ? "Disabled" : "Enabled"} successfully`);
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Toggle failed: " + (err.message || "Unknown error"));
    }
  };

  const handleDelete = async (reportKey) => {
    try {
      await ReportConfigService.delete(reportKey);
      message.success(`Deleted "${reportKey}"`);
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Delete failed: " + (err.message || "Unknown error"));
    }
  };

  function extractColumnFromClause(clause = "") {
    const m = clause.match(/AND\s+(\w+)\s/);
    return m ? m[1] : "";
  }

  // ── Table Columns ───────────────────────────────────────────────────────

  const columns = [
    {
      title: "#",
      width: 50,
      render: (_, __, idx) =>
        (pagination.current - 1) * pagination.pageSize + idx + 1,
    },
    {
      title: "Report Key",
      dataIndex: "reportKey",
      key: "reportKey",
      render: (val) => <span className="rcm-key-chip">{val}</span>,
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
      ellipsis: true,
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Datasource",
      dataIndex: "datasource",
      key: "datasource",
      width: 130,
      render: (val) =>
        val === "A3" ? (
          <Tag color="orange">A3</Tag>
        ) : (
          <Tag color="blue">REPLICATION</Tag>
        ),
    },
    {
      title: "Query Configuration",
      key: "sqlMode",
      width: 130,
      render: (_, record) =>
        record.downloadSql ? (
          <Tag color="purple">📦 Custom Download</Tag>
        ) : (
          <Tag color="default">Standard</Tag>
        ),
    },
    {
      title: "Filters",
      key: "filters",
      width: 90,
      render: (_, record) => {
        const count = Array.isArray(record.filterConfigs) ? record.filterConfigs.length : 0;
        return <Text style={{ color: "#4263eb" }}>{count} filter{count !== 1 ? "s" : ""}</Text>;
      },
    },
    {
      title: "API URLs",
      key: "urls",
      width: 180,
      render: (_, record) => {
        // Build query string from filterConfigs
        const filters = Array.isArray(record.filterConfigs) ? record.filterConfigs : [];
        const queryString = filters.length
          ? "?" + filters.map((f) => `${f.key}=`).join("&")
          : "";

        const baseDataUrl = `/api/v1/dynamic-data/${record.reportKey}`;
        const baseDlUrl = `/api/v1/dynamic-report/${record.reportKey}/download`;
        const dataUrl = baseDataUrl + queryString;
        const dlUrl = baseDlUrl + queryString;

        return (
          <div className="rcm-url-btns">
            <Tooltip
              title={
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {dataUrl}
                </span>
              }
              overlayStyle={{ maxWidth: 480 }}
            >
              <span className="rcm-url-btn" onClick={() => copyToClipboard(dataUrl)}>
                <CopyOutlined /> Data API
              </span>
            </Tooltip>
            <Tooltip
              title={
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {dlUrl}
                </span>
              }
              overlayStyle={{ maxWidth: 480 }}
            >
              <span className="rcm-url-btn dl" onClick={() => copyToClipboard(dlUrl)}>
                <DownloadOutlined /> Download
              </span>
            </Tooltip>
          </div>
        );
      },
    },

    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 90,
      render: (val) =>
        val ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="error">Inactive</Tag>
        ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 110,
      render: (val) =>
        val ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {String(val).substring(0, 10)}
          </Text>
        ) : (
          "—"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={`${record.isActive ? "Disable" : "Enable"} "${record.reportKey}"?`}
            onConfirm={() => handleToggle(record.reportKey, record.isActive)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              icon={<PoweroffOutlined />}
              type={record.isActive ? "default" : "primary"}
              danger={record.isActive}
            >
              {record.isActive ? "Disable" : "Enable"}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Permanently delete "${record.reportKey}"?`}
            onConfirm={() => handleDelete(record.reportKey)}
            okText="Delete"
            cancelText="Cancel"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="rcm-container">
      {/* Heading */}
      <div style={{ backgroundColor: "#fff", padding: "8px 16px", borderRadius: 8, marginBottom: 16 }}>
        <Heading title="Report Config Manager" navigateTo="/reports">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ marginLeft: "auto" }}
          >
            Add New Report
          </Button>
        </Heading>
      </div>

      {/* Stats */}
      <div className="rcm-stats">
        {[
          { icon: "📋", label: "Total Reports", val: stats.total, cls: "blue" },
          { icon: "✅", label: "Active", val: stats.active, cls: "green" },
          { icon: "⛔", label: "Inactive", val: stats.inactive, cls: "red" },
          { icon: "🔀", label: "Split SQL", val: stats.split, cls: "orange" },
        ].map((s) => (
          <div className="rcm-stat-card" key={s.label}>
            <div className={`rcm-stat-icon ${s.cls}`}>{s.icon}</div>
            <div>
              <div className="rcm-stat-val">{s.val}</div>
              <div className="rcm-stat-lbl">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="reportKey"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          size="middle"
        />
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal
        title={
          <div className="rcm-modal-header">
            <div className="rcm-modal-icon">
              <ApiOutlined />
            </div>
            <div className="rcm-modal-titles">
              <div className="rcm-modal-main">{editKey ? `Edit Config: ${editKey}` : "Add New Report Config"}</div>
              <div className="rcm-modal-sub">{editKey ? "Update report query, filters & settings" : "Set up a new dynamic data extraction endpoint"}</div>
            </div>
          </div>
        }
        open={modalOpen}
        onCancel={closeModal}
        width={860}
        className="rcm-premium-modal"
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
            icon={<DownloadOutlined />}
          >
            Save Config
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{ datasource: "REPLICATION", batchSize: 50000, isActive: true, filters: [] }}
        >
          {/* Row 1: Key + Name */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reportKey"
                label="Report Key"
                rules={[{ required: true, message: "Report Key is required" }]}
              >
                <Input
                  placeholder="e.g. booking-vs-audit-report"
                  disabled={!!editKey}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="displayName" label="Display Name">
                <Input placeholder="e.g. Booking vs Audit Report" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Datasource + Batch Size */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="datasource"
                label="Datasource"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="REPLICATION">REPLICATION (default)</Option>
                  <Option value="A3">A3</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="batchSize" label="Batch Size (rows per CSV fetch)">
                <InputNumber min={1000} max={500000} step={10000} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* SQL Section */}
          <div className="rcm-sql-container">
            <div className="rcm-section-title">SQL Queries</div>
            
            <Tabs defaultActiveKey="data" type="card" size="small" className="rcm-premium-tabs">
              <TabPane tab={<span>📊 Data API SQL *</span>} key="data">
                <Form.Item
                  name="sql"
                  rules={[{ required: true, message: "Data API SQL is required" }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea
                    rows={6}
                    placeholder={"SELECT col1, col2, hub_id\nFROM schema.table_name\nWHERE 1=1"}
                    className="rcm-textarea-dark"
                  />
                </Form.Item>
                {/* shouldUpdate ensures preview re-renders when setFieldsValue is called */}
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.sql !== cur.sql}>
                  {({ getFieldValue }) => <SqlPreview sql={getFieldValue("sql")} />}
                </Form.Item>
              </TabPane>

              <TabPane tab={<span>⬇️ Download SQL (optional)</span>} key="download">
                <Form.Item name="downloadSql" style={{ marginBottom: 0 }}>
                  <TextArea
                    rows={6}
                    placeholder={
                      "SELECT col1, col2\nFROM schema.table_name\nWHERE 1=1\n\n(Leave empty to use Data API SQL for download)"
                    }
                    className="rcm-textarea-dark"
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.downloadSql !== cur.downloadSql}>
                  {({ getFieldValue }) => <SqlPreview sql={getFieldValue("downloadSql")} />}
                </Form.Item>
              </TabPane>
            </Tabs>
          </div>

          {/* Filters */}
          <div style={{ marginTop: 16 }}>
            <div className="rcm-section-title">Filters (optional)</div>
            <Form.List name="filters">
              {(fields, { add, remove }) => (
                <>
                  {fields.length > 0 && (
                    <Row gutter={8} style={{ marginBottom: 6, padding: "0 4px" }}>
                      <Col span={7}><Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>Param Key</Text></Col>
                      <Col span={7}><Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>Type</Text></Col>
                      <Col span={7}><Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>DB Column</Text></Col>
                      <Col span={3} />
                    </Row>
                  )}
                  {fields.map(({ key, name, ...restField }) => (
                    <div className="rcm-filter-row" key={key} style={{ marginBottom: 8 }}>
                      <Form.Item {...restField} name={[name, "key"]} style={{ margin: 0 }}>
                        <Input placeholder="e.g. fromDate" size="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "type"]} style={{ margin: 0 }}>
                        <Select size="small">
                          <Option value="exact">exact</Option>
                          <Option value="date">date (from)</Option>
                          <Option value="date_to">date_to</Option>
                          <Option value="date_range">date_range</Option>
                          <Option value="list_integer">list_integer</Option>
                          <Option value="list_string">list_string</Option>
                          <Option value="integer">integer</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "column"]} style={{ margin: 0 }}>
                        <Input placeholder="e.g. hub_id" size="small" />
                      </Form.Item>
                      <MinusCircleOutlined
                        style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ key: "", type: "exact", column: "" })}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 4 }}
                  >
                    Add Filter
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* Status */}
          <div style={{ marginTop: 16 }}>
            <div className="rcm-section-title">Status</div>
            <Form.Item name="isActive" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ReportConfigManager;
